import { useEffect, useState, useCallback } from "react";
import { X, ArrowLeft } from "lucide-react";
import api, { photoUrl } from "@/lib/api";
import { Lightbox } from "@/components/public/Lightbox";

export const CategoryGallery = ({ category, onClose }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(-1);

  const load = useCallback(() => {
    api.get(`/photos?category_id=${category.id}`)
      .then(({ data }) => setPhotos(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category.id]);

  useEffect(() => {
    setLoading(true);
    load();
    const iv = setInterval(load, 15000);
    const onFocus = () => load();
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    document.body.style.overflow = "hidden";
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      document.body.style.overflow = "";
    };
  }, [load]);

  return (
    <div data-testid="category-gallery" className="fixed inset-0 z-[55] bg-cream overflow-y-auto animate-fade-up">
      <div className="sticky top-0 z-10 bg-cream border-b border-gold/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-4 flex items-center justify-between gap-4">
          <button data-testid="gallery-back" onClick={onClose} className="flex items-center gap-2 text-ink-soft hover:text-gold transition-colors">
            <ArrowLeft size={20} /> <span className="text-sm font-medium">Volver al portafolio</span>
          </button>
          <div className="text-right">
            <h2 className="font-serif text-2xl sm:text-3xl text-ink leading-none">{category.name}</h2>
            <p className="text-xs text-ink-muted mt-1">
              {photos.length} {photos.length === 1 ? "fotografía" : "fotografías"}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
        {loading ? (
          <p className="text-center text-ink-muted py-20">Cargando galería…</p>
        ) : photos.length === 0 ? (
          <p className="text-center text-ink-muted py-20">Aún no hay fotografías en esta categoría.</p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
            {photos.map((p, i) => (
              <button
                key={p.id}
                data-testid={`gallery-photo-${p.id}`}
                onClick={() => setLightbox(i)}
                className="img-zoom mb-6 block w-full rounded-sm overflow-hidden bg-beige card-hover break-inside-avoid group relative"
              >
                <img src={photoUrl(p, true)} alt={p.title || category.name} loading="lazy" className="w-full h-auto object-cover" />
                <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/10 transition-colors" />
              </button>
            ))}
          </div>
        )}
      </div>

      <button data-testid="gallery-close" onClick={onClose} className="fixed top-4 right-4 z-20 w-10 h-10 rounded-full bg-ink/90 text-cream flex items-center justify-center hover:bg-gold hover:text-ink transition-colors" aria-label="Cerrar galería">
        <X size={20} />
      </button>

      {lightbox >= 0 && (
        <Lightbox photos={photos} index={lightbox} onClose={() => setLightbox(-1)} onNav={setLightbox} />
      )}
    </div>
  );
};
